import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthToken } from '../services/apiClient';
import { getAllUsers, deleteUser, isCurrentUserAdmin, UserDto } from '../services/users';
import { getMenuByUser } from '../services/menus';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PeopleIcon from '@mui/icons-material/People';

type MenuConfigStatus = 'completed' | 'in-progress' | 'not-started';

function renderMenuStatus(status: MenuConfigStatus | undefined) {
  if (!status) {
    return <CircularProgress size={16} />;
  }
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="success" titleAccess="Konfigurace menu dokončena" />;
    case 'in-progress':
      return <HourglassEmptyIcon color="warning" titleAccess="Konfigurace menu rozpracována" />;
    case 'not-started':
      return <CancelIcon color="error" titleAccess="Konfigurace menu nezačala" />;
  }
}

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [menuStatuses, setMenuStatuses] = useState<Map<string, MenuConfigStatus>>(new Map());

  useEffect(() => {
    // Check if user is logged in
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }

    // Check if user is admin
    if (!isCurrentUserAdmin()) {
      setError('Přístup odepřen. Tato stránka je pouze pro administrátory.');
      setLoading(false);
      return;
    }

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllUsers();
        
        // Normalize potential $values arrays
        const normalizedUsers = Array.isArray(data)
          ? data
          : Array.isArray((data as { $values?: UserDto[] })?.$values)
            ? (data as { $values?: UserDto[] }).$values!
            : [];

        setUsers(normalizedUsers);
      } catch (err: unknown) {
        console.error('Error fetching users:', err);
        const error = err as { status?: number };
        if (error.status === 403) {
          setError('Přístup odepřen. Nemáte oprávnění pro tuto akci.');
        } else {
          setError('Nepodařilo se načíst seznam uživatelů. Zkuste to prosím znovu.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [navigate]);

  useEffect(() => {
    if (users.length === 0) return;

    let cancelled = false;
    const fetchMenuStatuses = async () => {
      const results = await Promise.all(
        users.map(async (user): Promise<[string, MenuConfigStatus]> => {
          try {
            const menu = await getMenuByUser(user.id);
            if (menu?.configuration?.isCompleted) {
              return [user.id, 'completed'];
            }
            return [user.id, menu ? 'in-progress' : 'not-started'];
          } catch {
            return [user.id, 'not-started'];
          }
        })
      );
      if (!cancelled) {
        setMenuStatuses(new Map(results));
      }
    };

    fetchMenuStatuses();
    return () => { cancelled = true; };
  }, [users]);

  const handleDeleteClick = (user: UserDto) => {
    setUserToDelete(user);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteUser(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: unknown) {
      console.error('Error deleting user:', err);
      const error = err as { details?: { message?: string } };
      setDeleteError(error.details?.message || 'Nepodařilo se smazat uživatele.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeleteError(null);
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            Správa uživatelů
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Zobrazení a správa registrovaných uživatelů
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          label="Hledat uživatele"
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Hledat podle e-mailu nebo uživatelského jména"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper sx={{ mb: 2, p: 2, backgroundColor: 'action.hover' }}>
        <Typography variant="body2" color="text.secondary">
          Celkem uživatelů: <strong>{users.length}</strong>
          {searchTerm && ` | Zobrazeno: ${filteredUsers.length}`}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>E-mail</TableCell>
              <TableCell>Uživatelské jméno</TableCell>
              <TableCell align="center">E-mail potvrzen</TableCell>
              <TableCell align="center">Konfigurace menu</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Registrace</TableCell>
              <TableCell align="center">Akce</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.userName}</TableCell>
                <TableCell align="center">
                  {user.emailConfirmed ? (
                    <CheckCircleIcon color="success" titleAccess="Potvrzeno" />
                  ) : (
                    <CancelIcon color="error" titleAccess="Nepotvrzeno" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {renderMenuStatus(menuStatuses.get(user.id))}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.roles.map((role, idx) => (
                      <Chip
                        key={idx}
                        label={role}
                        size="small"
                        color={role === 'Admin' ? 'error' : 'primary'}
                        variant={role === 'Admin' ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(user.createdOn)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(user)}
                    disabled={user.roles.includes('Admin')}
                    title={user.roles.includes('Admin') ? 'Nelze smazat administrátora' : 'Smazat uživatele'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredUsers.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {searchTerm ? 'Žádní uživatelé nenalezeni.' : 'Žádní uživatelé k dispozici.'}
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Smazat uživatele</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Opravdu chcete smazat uživatele <strong>{userToDelete?.email}</strong>?
            <br />
            Tato akce je nevratná.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Zrušit
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Smazat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
